import { Box, Button, CircularProgress } from "@mui/material";
import { useState } from "react";
import { useEventsApi } from "../hooks/useEventsApi";
import { config } from "../config";
import CustomAlert from "./CustomAlert";

interface ExportTextButtonProps {
  onStart: () => void;
  onEnd: () => void;
  language: string; // z. B. "eng" oder "ger"
  fileExtension: string; // URL segment: edf, pdf, kollmorgen, …
  mode: string; // approved, valid, all
  use_old_code: string; // true, false
  disabledExternally?: boolean;
  /** Overrides download filename (e.g. kollmorgen_optx2_messages.txt) */
  downloadFileName?: string;
  /** Overrides Blob MIME type (e.g. text/plain;charset=utf-8) */
  blobMimeType?: string;
  /** Overrides button label (default: fileExtension) */
  buttonLabel?: string;
  /** GET returns application/zip Blob (callApi) instead of plain text */
  expectZipResponse?: boolean;
  /** Omit " (LANG)" after the button label */
  hideLanguageInLabel?: boolean;
  /** Number of polling attempts before timing out */
  maxAttempts?: number;
  /** Delay in milliseconds between polling attempts */
  pollingDelayMs?: number;
  /** Show "Export " prefix before label (default true). */
  showExportPrefix?: boolean;
}

const ExportTextButton: React.FC<ExportTextButtonProps> = ({
  onStart,
  onEnd,
  language,
  fileExtension,
  mode,
  use_old_code,
  disabledExternally,
  downloadFileName,
  blobMimeType,
  buttonLabel,
  expectZipResponse,
  hideLanguageInLabel,
  maxAttempts = 20,
  pollingDelayMs = 3000,
  showExportPrefix = true,
}) => {
  const { callApi } = useEventsApi();
  const [loading, setLoading] = useState(false);

  const handleExport = async () => {
    setLoading(true);
    onStart();
    try {
      // 1. Export starten
      const startResponse = await callApi(
        `${config.apiUrl}/api/start-export-${fileExtension}/?lang=${language}&mode=${mode}&use_old_code=${use_old_code}`,
        "POST"
      );

      const export_id = startResponse?.export_id;
      if (!export_id) {
        throw new Error("Export could not be started.");
      }

      // 2. Polling bis Datei verfügbar ist
      let attempts = 0;
      const delay = (ms: number) => new Promise(res => setTimeout(res, ms));

      let blob: Blob | null = null;

      while (attempts < maxAttempts) {
        await delay(pollingDelayMs);

        try {
          const data = await callApi(
            `${config.apiUrl}/api/download-export-${fileExtension}/${export_id}/`,
            "GET"
          );

          if (data instanceof Blob) {
            blob = data;
          } else {
            const mime =
              blobMimeType ?? `application/${fileExtension}`;
            blob = new Blob([data as BlobPart], { type: mime });
          }
          // Backend may briefly expose a 0-byte file while the export finishes writing.
          if (blob.size < 1) {
            console.warn(`Versuch ${attempts + 1}: Download empty, retrying`);
            attempts++;
            continue;
          }
          break;
        } catch (err: any) {
          if (err?.status && err.status !== 404) {
            throw err;
          }
          console.warn(`Versuch ${attempts + 1}: Export not available`, err);
        }

        attempts++;
      }

      if (!blob) {
        throw new Error("Export not available or took too long.");
      }

      // 3. Datei herunterladen
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        downloadFileName ?? `events_export.${fileExtension}`
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error("Export failed:", error);
      setAlertMessage(`Export failed: ${String(error)}`);
      setAlertSeverity("error");
      setAlertOpen(true);
    } finally {
      setLoading(false);
      onEnd();
    }
  };

  const [alertOpen, setAlertOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertSeverity, setAlertSeverity] = useState<"error" | "success">("error");

  return (
    <Box>
      <Button
        onClick={handleExport}
        disabled={loading || disabledExternally}
        startIcon={loading ? <CircularProgress size={20} /> : null}
      >
        {`${showExportPrefix ? "Export " : ""}${buttonLabel ?? fileExtension}`.trim()}
        {!hideLanguageInLabel && ` (${language.toUpperCase()})`}
      </Button>
      <CustomAlert
        open={alertOpen}
        onClose={() => setAlertOpen(false)}
        message={alertMessage}
        severity={alertSeverity}
      />
    </Box>
  );
};

export default ExportTextButton